import type { ComponentType } from "react"
import dynamic from "next/dynamic"
import { SENJA_BLOK_COMPONENT } from "@/lib/senja-blok-tree"

// ---------------------------------------------------------------------------
// Eager (kept in the page chunk): the container, navigation, footer, and every
// above-the-fold hero / header / navbar variant. These render first paint and
// gate the A/B experiment surface, so they must not be code-split.
// ---------------------------------------------------------------------------
import { HomeHero } from "../app/sections/hero"
import Footer from "./static/Footer"
import Page from "./containers/Page"
import PageNav, {
  NavCategoryItem,
  SubNavCategoryItem,
} from "./sections/PageNav"
import Breadcrumb, { BreadcrumbItem } from "./sections/Breadcrumb"
import HeroSection from "./home-page/HeroSection"
import PetTransportationHeader from "./sections/pet-transportation/PetTransportationHeader"
import PillarsHeader from "./sections/pillars/PillarsHeader"
import PageHeader2 from "./sections/PageHeader2"
import PageHeader3 from "./sections/PageHeader3"
import PremiumHero from "../components/premium/hero"
import PremiumNavbar from "../components/premium/navbar"
import PremiumCategoryNavbar from "../components/premium/category-navbar"
import ProductHero from "../components/product/hero"
import ProductHeroVariant from "../components/product/hero-variant"
import ProductNavbar from "../components/product/navbar"
import PetsHero from "./sections/PetsHero"
import PetsHeroB from "./sections/PetsHeroB"
import PartnersHero from "../components/partners/hero"
import PetsHeroATF from "../components/premium/heroATF"
import PetsHeroATFVariant from "../components/premium/heroATFVariant"
import PetsHeroATFV4 from "../components/premium/heroATFV4"
import PetsHeroATFV5 from "../components/premium/heroATFV5"
import PetsHeroATFTestimonial from "@/components/premium/heroATFTestimonial"
import PetsHeroATFYoutube from "@/components/premium/PetsHeroATFYoutube"
import PetsHeroATFFeatured from "@/components/premium/heroATFFeatured"

// ---------------------------------------------------------------------------
// Code-split (next/dynamic, ssr: true — identical SSR HTML, no CLS/SEO change).
// Below-the-fold sections and leaf item components. Every catch-all page ships
// only the chunks for the components its story actually uses, instead of the
// whole CMS library. Registry keys below stay byte-for-byte identical.
// ---------------------------------------------------------------------------
const PageHeader = dynamic(() => import("./sections/PageHeader"))
const CostFactors = dynamic(() => import("./sections/cost-to-ship/CostFactors"))
const TransportCost = dynamic(
  () => import("./sections/cost-to-ship/TransportCost"),
)
const CostCard = dynamic(() => import("./sections/cost-to-ship/TransportCost"))
const CustomerReviews = dynamic(() => import("./sections/CustomerReviews"))
const PolaroidsHeader = dynamic(() => import("./sections/PolaroidsHeader"))
const FAQS = dynamic(() => import("./sections/FAQs"))
const MeetTheDrivers = dynamic(() => import("./sections/MeetTheDrivers"))
const Testimonials = dynamic(
  () => import("../components/testimonials/Testimonials"),
)
const ThumbnailSet = dynamic(() => import("./sections/ThumbnailSet"))
const CardSet = dynamic(() => import("./sections/CardSet"))
const PetShippingCardSet = dynamic(
  () => import("./sections/PetShippingCardSet"),
)
const OfferCards = dynamic(() => import("../components/cards/OfferCard"))
const OfferCardItem = dynamic(() =>
  import("../components/cards/OfferCard").then((m) => m.OfferCardItem),
)

const Container = dynamic(() => import("./containers/Container"))
const AdaptiveGrid = dynamic(() => import("./sections/layout/AdaptiveGrid"))
const ArticlesSection = dynamic(() => import("./sections/ArticlesSection"))
const AsFeaturedIn = dynamic(() => import("./static/AsFeaturedIn"))
const FeaturedInItem = dynamic(() =>
  import("./static/AsFeaturedIn").then((m) => m.FeaturedInItem),
)
const Button = dynamic(() => import("./sections/Button"))
const CTAVariant = dynamic(() => import("./sections/CTAVariant"))
const DriverFitChecklist = dynamic(
  () => import("./sections/DriverFitChecklist"),
)
const Calculator = dynamic(() => import("./sections/Calculator"))
const CheapestAutoTransport = dynamic(
  () => import("./sections/car-shipping/CheapestAutoTransport"),
)
const CheckList = dynamic(() => import("./sections/CheckList"))
const ChoosingShippers = dynamic(() => import("./sections/ChoosingShippers"))
const CostBreakdown = dynamic(() => import("./sections/CostBreakdown"))
const Coverage = dynamic(() => import("./static/Coverage"))
const DropdownSection = dynamic(() => import("./sections/DropdownSection"))
const ExpenseCard = dynamic(() => import("../components/cards/ExpenseCard"))
const Factors = dynamic(() => import("./sections/Factors"))
const Features = dynamic(() => import("./sections/Features"))
const GetQuotes = dynamic(() => import("./sections/GetQuotes"))
const HeaderDescImage = dynamic(() => import("./sections/HeaderDescImage"))
const Heading = dynamic(() => import("./sections/Heading"))
const HowMuchDoesItCostToMoveAnRV = dynamic(
  () => import("./sections/rv-transport/HowMuchDoesItCostToMoveAnRV"),
)
const ImageLeftContent = dynamic(() => import("./sections/ImageLeftContent"))
const ImageRightContent = dynamic(() => import("./sections/ImageRightContent"))
const Introduction = dynamic(() => import("./sections/Introduction"))
const LFLReviewCard = dynamic(() => import("../components/cards/LFLReviewCard"))
const LFLReviews = dynamic(() => import("./sections/LFLReviews"))
const LeadingThePack = dynamic(
  () => import("./sections/pet-transportation/LeadingThePack"),
)
const ListSubsection = dynamic(() => import("./ListSubsection"))
const LinkBlock = dynamic(() => import("../components/link-block"))
const LinkBlockItem = dynamic(() =>
  import("../components/link-block").then((m) => m.LinkBlockItem),
)
const MarkdownAndDetails = dynamic(
  () => import("./sections/MarkdownAndDetails"),
)
const MarkdownAndTable = dynamic(() => import("./sections/MarkdownAndTable"))
const MarkdownSection = dynamic(() => import("./sections/MarkdownSection"))
const MotoIntro = dynamic(() => import("./sections/MotoIntro"))
const NoHassleCTA = dynamic(() => import("./sections/NoHassleCTA"))
const OurPromise = dynamic(
  () => import("./sections/pet-transportation/OurPromise"),
)
const PetsPassion = dynamic(() => import("./sections/Passion"))
const PillarsArticle = dynamic(
  () => import("./sections/pillars/PillarsArticle"),
)
const PillarsReview = dynamic(() => import("./sections/pillars/PillarReview"))
const PillarsSubReview = dynamic(
  () => import("./sections/pillars/PillarsSubReview"),
)
const PillarsTrustPilot = dynamic(
  () => import("./sections/pillars/PillarsTrustPilot"),
)
const PillarsLeadingPack = dynamic(
  () => import("./sections/pillars/PillarsLeadingPack"),
)
const PillarsOurPromise = dynamic(
  () => import("./sections/pillars/PillarsOurPromise"),
)
const PillarsFAQ = dynamic(() => import("./sections/pillars/PillarsFAQ"))
const PillarsPeaceOfMind = dynamic(
  () => import("./sections/pillars/PillarsPeaceOfMind"),
)
const PillarsMeetDrivers = dynamic(
  () => import("./sections/pillars/PillarsMeetDrivers"),
)
const PillarSingleReviewCard = dynamic(
  () => import("./sections/pillars/PillarSingleReviewCard"),
)
const Preparation = dynamic(() => import("./sections/Preparation"))
const PreparingForCarDelivery = dynamic(
  () => import("./sections/car-shipping/PreparingForCarDelivery"),
)
const PreparingForHouseholdGoodsShipping = dynamic(
  () =>
    import(
      "./sections/household-goods-shipping/PreparingForHouseholdGoodsShipping"
    ),
)
const Process = dynamic(() => import("./sections/Process"))
const RVInvestment = dynamic(
  () => import("./sections/rv-transport/RVInvestment"),
)
const RatingsBlock = dynamic(() => import("./sections/RatingsBlock"))
const Section = dynamic(() => import("./sections/Section"))
const SenjaTestimonials = dynamic(() => import("./sections/SenjaTestimonials"))
const ShipFactors = dynamic(() => import("./sections/ShipFactors"))
const ShipSteps = dynamic(() => import("./sections/ShipSteps"))
const Stats = dynamic(() => import("./static/Stats"))
const SuccessStories = dynamic(
  () => import("./sections/cost-to-ship/SuccessStories"),
)
const Support = dynamic(() => import("./sections/Support"))
const TableColumnSection = dynamic(
  () => import("./sections/TableColumnSection"),
)
const TableSection = dynamic(() => import("./sections/layout/TableSection"))
const TextColumnsSection = dynamic(
  () => import("./sections/TextColumnsSection"),
)
const WaysToShip = dynamic(() => import("./sections/WaysToShip"))
const YoutubeEmbed = dynamic(() => import("./sections/YoutubeEmbed"))
const ArticleVideo = dynamic(() => import("./sections/ArticleVideo"))
const MeetTheDriversCard = dynamic(
  () => import("../components/cards/MeetTheDriverCard"),
)
const FeatureCards = dynamic(() => import("../components/cards/FeatureCard"))
const FeatureCardItem = dynamic(() =>
  import("../components/cards/FeatureCard").then((m) => m.FeatureCardItem),
)
const MeetTheDriversV2 = dynamic(
  () => import("./sections/vehicle-transportation/MeetTheDrversV2"),
)
const ReviewSnippets = dynamic(
  () => import("./sections/vehicle-transportation/ReviewSnippet"),
)
const ReviewSnippetsCard = dynamic(() =>
  import("./sections/vehicle-transportation/ReviewSnippet").then(
    (m) => m.ReviewSnippetsCard,
  ),
)
const HomeContent = dynamic(() => import("./home-page/HomeContent"))
const HomePriceShowCaseItem = dynamic(() =>
  import("./home-page/HomeContent").then((m) => m.HomePriceShowCaseItem),
)
const HomeReviewItem = dynamic(() =>
  import("./home-page/HomeContent").then((m) => m.HomeReviewItem),
)
const HomeMeetTheDrivers = dynamic(() => import("./home-page/HomeMeetDrivers"))
const HomeMeetDriversCard = dynamic(() =>
  import("./home-page/HomeMeetDrivers").then((m) => m.HomeMeetDriversCard),
)
const HomeTestimony = dynamic(() =>
  import("./home-page/HomeMeetDrivers").then((m) => m.HomeTestimony),
)
const HomeSupport = dynamic(() => import("./home-page/HomeSupport"))
const HomeSupportItem = dynamic(() =>
  import("./home-page/HomeSupport").then((m) => m.HomeSupportItem),
)
const PremiumAsFeaturedIn = dynamic(
  () => import("../components/premium/as-featured-in"),
)
const PremiumPriceTag = dynamic(() => import("../components/premium/price-tag"))
const PremiumAuthorReview = dynamic(
  () => import("../components/premium/author-review"),
)
const PremiumPetOnTheRoad = dynamic(
  () => import("../components/premium/pet-on-the-road"),
)
const PremiumOurPromise = dynamic(
  () => import("../components/premium/our-promise"),
)
const PremiumStats = dynamic(() => import("../components/premium/stats"))
const PremiumFooter = dynamic(() => import("../components/premium/footer"))
const WhyCitizenshipper = dynamic(
  () => import("../components/product/why-citizenshipper"),
)
const Services = dynamic(() => import("../components/product/services"))
const ProductAsFeaturedIn = dynamic(
  () => import("../components/product/as-featured-in"),
)
const ProductFooter = dynamic(() => import("../components/product/footer"))
const PartnersSection = dynamic(() => import("../components/partners/section"))
const BusinessOpportunityForm = dynamic(
  () => import("../components/partners/form"),
)
const HowMuchTable = dynamic(() => import("./sections/HowMuchTable"))
const HowMuchQuote = dynamic(() => import("./sections/HowMuchQuote"))
const PreviousShipments = dynamic(
  () => import("./sections/new-drivers/previous-shipments"),
)
const HowCSWorks = dynamic(() => import("./sections/HowCSWorks"))
const WhyChooseCS = dynamic(() => import("./sections/WhyChooseCS"))
const DriversRoutes = dynamic(
  () => import("../components/routes-module/drivers-routes"),
)

// Annotated to erase each component's private prop interface from the exported
// type — dynamic() surfaces those in inference and TS can't name unexported ones
// (TS4023). storyblokInit resolves components by string key at runtime.
const components: Record<string, ComponentType<any>> = {
  page: Page,
  container: Container,
  pageNav: PageNav,
  pageHeader: PageHeader,
  customerReviews: CustomerReviews,
  meetTheDrivers: MeetTheDrivers,
  process: Process,
  checkList: CheckList,
  getQuotes: GetQuotes,
  FAQs: FAQS,
  ratingsBlock: RatingsBlock,
  introduction: Introduction,
  asFeaturedIn: AsFeaturedIn,
  FeaturedInItem,
  waysToShip: WaysToShip,
  LFLReviews: LFLReviews,
  LFLReviewCard: LFLReviewCard,
  choosingShippers: ChoosingShippers,
  shipFactors: ShipFactors,
  shipSteps: ShipSteps,
  markdownAndDetails: MarkdownAndDetails,
  calculator: Calculator,
  stats: Stats,
  cheapestAutoTransport: CheapestAutoTransport,
  motoIntro: MotoIntro,
  preparation: Preparation,
  support: Support,
  mdSection: MarkdownSection,
  noHassleCTA: NoHassleCTA,
  preparingForCarDelivery: PreparingForCarDelivery,
  factors: Factors,
  howToShipHouseholdGoods: PreparingForHouseholdGoodsShipping,
  rvInvestment: RVInvestment,
  howMuchDoesItCostToMoveAnRV: HowMuchDoesItCostToMoveAnRV,
  markdownAndTable: MarkdownAndTable,
  petTransportationHeader: PetTransportationHeader,
  ourPromise: OurPromise,
  imageLeftContent: ImageLeftContent,
  imageRightContent: ImageRightContent,
  section: Section,
  // Same key inspectSenjaStory matches, so fallback suppression and rendering
  // cannot drift apart.
  [SENJA_BLOK_COMPONENT]: SenjaTestimonials,
  costBreakdown: CostBreakdown,
  footer: Footer,
  dropdownSection: DropdownSection,
  listSubsection: ListSubsection,
  youtubeEmbed: YoutubeEmbed,
  articleVideo: ArticleVideo,
  articlesSection: ArticlesSection,
  tableSection: TableSection,
  adaptiveGrid: AdaptiveGrid,
  leadingThePack: LeadingThePack,
  polaroidsHeader: PolaroidsHeader,
  transportCost: TransportCost,
  costFactors: CostFactors,
  successStories: SuccessStories,
  expenseCard: ExpenseCard,
  headerDescImage: HeaderDescImage,
  textColumnsSection: TextColumnsSection,
  tableColumnSection: TableColumnSection,
  CTAVariant,
  driverFitChecklist: DriverFitChecklist,
  Coverage,
  Button,
  heading: Heading,
  pillarsHeader: PillarsHeader,
  pillarsArticle: PillarsArticle,
  pillarsReview: PillarsReview,
  PillarsSubReview,
  PillarsTrustPilot,
  PillarsOurPromise,
  PillarsLeadingPack,
  PillarsPeaceOfMind,
  PillarsMeetDrivers,
  PillarSingleReviewCard,
  PillarsFAQ,
  MeetTheDriversV2,
  MeetTheDriversCard,
  ReviewSnippets,
  ReviewSnippetsCard,
  HomeContent,
  HeroSection,
  HomeReviewItem,
  HomePriceShowCaseItem,
  HomeTestimony,
  HomeMeetTheDrivers,
  HomeMeetDriversCard,
  HomeSupport,
  HomeSupportItem,
  NavCategoryItem,
  SubNavCategoryItem,
  BreadcrumbItem,
  Breadcrumb,
  LinkBlock,
  LinkBlockItem,
  PageHeader2,
  PageHeader3,
  HomeHero,
  FeatureCards,
  FeatureCardItem,
  OfferCards,
  OfferCardItem,
  PremiumHero,
  PremiumNavbar,
  PremiumCategoryNavbar,
  PremiumAsFeaturedIn,
  PremiumPriceTag,
  PremiumAuthorReview,
  PremiumPetOnTheRoad,
  PremiumOurPromise,
  PremiumStats,
  PremiumFooter,
  WhyCitizenshipper,
  Services,
  ProductHero,
  ProductHeroVariant,
  ProductFooter,
  ProductNavbar,
  ProductAsFeaturedIn,
  ThumbnailSet,
  CardSet,
  PetsHero,
  Features,
  PartnersHero,
  PartnersSection,
  PetShippingCardSet,
  PetsPassion,
  BusinessOpportunityForm,
  CostCard,
  Testimonials,
  PetsHeroB,
  PetsHeroATF,
  PetsHeroATFVariant,
  PetsHeroATFV4,
  PetsHeroATFV5,
  PetsHeroATFTestimonial,
  PetsHeroATFYoutube,
  PetsHeroATFFeatured,
  HowMuchTable,
  HowMuchQuote,
  PreviousShipments,
  HowCSWorks,
  WhyChooseCS,
  DriversRoutes,
}

export default components
export type {
  BlokImage,
  IPageBlok,
  IStoryBlokComponent,
  BlokTable,
  ConditionalBlok,
} from "./types"
